import type { ColumnPinningState } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import type { DragHandleBinding } from '../types';
const DRAG_ID_PREFIX = 'column-reorder-drag:' as const;
const DROP_ID_PREFIX = 'column-reorder-drop:' as const;
export const createColumnDragId = (columnId: string): string => `${DRAG_ID_PREFIX}${columnId}`;
export const createColumnDropId = (columnId: string): string => `${DROP_ID_PREFIX}${columnId}`;
export const parseColumnIdFromDragId = (id: string | number): string | null => {
  const s = String(id);
  if (!s.startsWith(DRAG_ID_PREFIX)) {
    return null;
  }
  return s.slice(DRAG_ID_PREFIX.length);
};
export const parseColumnIdFromDropId = (id: string | number): string | null => {
  const s = String(id);
  if (!s.startsWith(DROP_ID_PREFIX)) {
    return null;
  }
  return s.slice(DROP_ID_PREFIX.length);
};
const arrayMoveIds = (order: string[], fromIndex: number, toIndex: number): string[] => {
  const next = [...order];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

/** Ensures visual order is [ …leftPinned…, …center…, …rightPinned… ] matching TanStack expectations. */
export const normalizeColumnOrderForPinning = (proposedOrder: string[], pinning: ColumnPinningState): string[] => {
  const left = new Set(pinning.left ?? []);
  const right = new Set(pinning.right ?? []);
  const leftOrdered = proposedOrder.filter(id => left.has(id));
  const rightOrdered = proposedOrder.filter(id => right.has(id));
  const centerOrdered = proposedOrder.filter(id => !left.has(id) && !right.has(id));
  return [...leftOrdered, ...centerOrdered, ...rightOrdered];
};
interface ReorderColumnsOnDropArgs {
  columnOrder: string[];
  activeColumnId: string;
  overColumnId: string;
  pinning: ColumnPinningState;
  allowColumnPinning: boolean;
}
interface ReorderColumnsOnDropResult {
  columnOrder: string[];
  columnPinning: ColumnPinningState;
}

/**
 * Moves `activeColumnId` to `overColumnId`'s index, then normalizes for pinning.
 * When pinning is allowed: dropping onto a left-pinned column pins the active column on the left;
 * dropping onto a right-pinned column pins it on the right.
 */
export const reorderColumnsOnDrop = ({
  columnOrder,
  activeColumnId,
  overColumnId,
  pinning,
  allowColumnPinning
}: ReorderColumnsOnDropArgs): ReorderColumnsOnDropResult => {
  const activeIndex = columnOrder.indexOf(activeColumnId);
  const overIndex = columnOrder.indexOf(overColumnId);
  if (activeIndex === -1 || overIndex === -1 || activeColumnId === overColumnId) {
    return {
      columnOrder,
      columnPinning: pinning
    };
  }
  const movedOrder = arrayMoveIds(columnOrder, activeIndex, overIndex);
  if (!allowColumnPinning) {
    return {
      columnOrder: normalizeColumnOrderForPinning(movedOrder, pinning),
      columnPinning: pinning
    };
  }
  const leftHas = new Set(pinning.left ?? []);
  const rightHas = new Set(pinning.right ?? []);
  if (leftHas.has(activeColumnId)) {
    if (!leftHas.has(overColumnId)) {
      leftHas.delete(activeColumnId);
      if (rightHas.has(overColumnId)) {
        rightHas.add(activeColumnId);
      }
    }
  } else if (rightHas.has(activeColumnId)) {
    if (!rightHas.has(overColumnId)) {
      rightHas.delete(activeColumnId);
      if (leftHas.has(overColumnId)) {
        leftHas.add(activeColumnId);
      }
    }
  }
  const columnPinning: ColumnPinningState = {
    left: movedOrder.filter(id => leftHas.has(id)),
    right: movedOrder.filter(id => rightHas.has(id))
  };
  return {
    columnOrder: normalizeColumnOrderForPinning(movedOrder, columnPinning),
    columnPinning
  };
};
export const wrapHeaderWithDNDActivator = (drag: DragHandleBinding | undefined, children: ReactNode): ReactNode => {
  if (drag == null) {
    return children;
  }
  return <span ref={drag.setActivatorNodeRef} {...drag.listeners} {...drag.attributes} style={{
    cursor: 'grab',
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    touchAction: 'none'
  }}>
      {children}
    </span>;
};